'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Breadcrumbs } from '../ui/breadcrumbs';
import { Paragraph } from '../ui/paragraph';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function FirstBlog({ blog, locale }: { blog: Blog; locale: string }) {
  const { t } = useTranslations();
  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <section className={cn('grid w-full grid-cols-1 gap-0 md:grid-cols-2')}>
      <div
        className={cn(
          'col-span-1 flex h-full w-full flex-col items-end justify-start gap-6 bg-primary-foreground py-8 pl-5 pr-5 sm:py-10 lg:py-14 lg:pr-16',
        )}
      >
        <div className="flex h-full w-full flex-col items-start justify-between gap-6 md:max-w-[600px]">
          <Breadcrumbs breadcrumbs={[{ label: 'Blog', href: '/blog' }]} />
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <Paragraph size="md" color="muted-foreground">
              {formattedDate}
            </Paragraph>
            <Heading tag="h2" semanticTag="h1" title={blog.name} className="w-full max-w-[540px]" />
          </div>
          <Button asChild variant={'dark'} className="rounded-full">
            <Link href={blog.slug}>{t('text1148')}</Link>
          </Button>
        </div>
      </div>
      <div className="relative col-span-1 flex aspect-square w-full items-end justify-start sm:aspect-auto sm:h-[600px] md:h-[500px] lg:h-[730px]">
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          loading="eager"
          sizes="100vw"
        />
      </div>
    </section>
  );
}
